import {Component, Input, OnInit, OnChanges, OnDestroy, SimpleChanges, ViewChild} from '@angular/core';
import {MatTableDataSource} from "@angular/material/table";
import {MatPaginator} from "@angular/material/paginator";
import {MatSort} from "@angular/material/sort";
import {LogService} from "../../../../../services/log.service";
import {Router} from "@angular/router";
import {PermissionType} from "../../../../../components/employee/PermissionType.enum";
import {GroupSessionTopics} from "../group-session-topics.model";
import {GroupSessionTopicsService} from "../group-session-topics.service";
import {
  AddEditGroupSessionTopicsComponent
} from "../add-edit-group-session-topics/add-edit-group-session-topics.component";
import {MatDialog} from "@angular/material/dialog";
import {Subject} from "rxjs";
import {takeUntil} from "rxjs/operators";

@Component({
  selector: 'app-group-session-topics-list',
  templateUrl: './group-session-topics-list.component.html',
  styleUrls: ['./group-session-topics-list.component.scss']
})
export class GroupSessionTopicsListComponent implements OnInit, OnChanges, OnDestroy {
  private _destroyed$ = new Subject<void>();
  displayedColumns: string[] = [
    'srNo',
    'content',
    'detailedDescription',
    'presentationUrls',
    'description',
    'actions'
  ];

  dataSource = new MatTableDataSource<GroupSessionTopics>([]);

  @Input() isVisibilePresentational: boolean = false;
  /** Passed by the parent presentation component — kept for API consistency */
  @Input() selectedCentreCodes: string[] = [];
  @Input() startDate: Date | null = null;
  @Input() endDate: Date | null = null;
  @Input() isNestedComponent: boolean = false;

  /** When provided, the table auto-filters to only show rows whose content matches one of these topics */
  @Input() coveredTopics: string[] = [];

  uniqueContents: string[] = [];
  selectedContents: string[] = [];
  allData: GroupSessionTopics[] = [];

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    private groupSessionService: GroupSessionTopicsService,
    private mLogService : LogService,
    private router: Router,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.updateDisplayedColumns();
    this.loadList();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['isVisibilePresentational']) {
      this.updateDisplayedColumns();
    }

    // When coveredTopics input changes and data is already loaded, apply the filter
    if (changes['coveredTopics'] && this.allData.length > 0) {
      this.applyCoveredTopicsFilter();
    }
  }

  updateDisplayedColumns(): void {
    if (this.isVisibilePresentational) {
      // Hide detailedDescription and presentationUrls columns
      this.displayedColumns = [
        'srNo',
        'content',
        'description',
      ];
    } else {
      // Show all columns
      this.displayedColumns = [
        'srNo',
        'content',
        'detailedDescription',
        'presentationUrls',
        'description',
        'actions'
      ];
    }
  }


  loadList() {
    this.groupSessionService.getAll().pipe(takeUntil(this._destroyed$)).subscribe((data: any) => {
      // Store all data
      this.allData = data;

      // Debug: Check data structure
      console.log('=== GROUP SESSION TOPICS DATA ===');
      console.log('Total records:', data.length);

      // Extract unique content values
      this.uniqueContents = [...new Set(data.map((item: GroupSessionTopics) => item.content).filter((content: string) => content))] as string[];

      if (data && data.length > 0) {
        data.forEach((item: any, index: number) => {
          console.log(`\n--- Record ${index + 1}: ${item.content} ---`);
          console.log('presentationUrls:', item.presentationUrls);
          console.log('Type:', typeof item.presentationUrls);
          console.log('Is Array:', Array.isArray(item.presentationUrls));
          if (Array.isArray(item.presentationUrls)) {
            console.log('Array length:', item.presentationUrls.length);
            console.log('Array items:', item.presentationUrls);
          }
        });
      }

      // If coveredTopics are provided, apply the filter; otherwise show all
      if (this.coveredTopics && this.coveredTopics.length > 0) {
        this.applyCoveredTopicsFilter();
      } else {
        this.dataSource.data = data;
      }
      this.dataSource.paginator = this.paginator;
      this.dataSource.sort = this.sort;
    });
  }

  /**
   * Filter the table to only show rows whose `content` matches one of the coveredTopics.
   * Also auto-selects the matching topics in the dropdown filter so they appear pre-selected.
   */
  private applyCoveredTopicsFilter(): void {
    if (!this.coveredTopics || this.coveredTopics.length === 0) {
      this.dataSource.data = this.allData;
      this.selectedContents = [];
      return;
    }
    const coveredSet = new Set(this.coveredTopics.map(t => t.trim().toLowerCase()));

    // Auto-select matching topics in the filter dropdown
    this.selectedContents = this.uniqueContents.filter(
      content => coveredSet.has(content.trim().toLowerCase())
    );

    // Filter table rows
    this.dataSource.data = this.allData.filter(item =>
      item.content && coveredSet.has(item.content.trim().toLowerCase())
    );
  }

  filterByContent(): void {
    if (this.selectedContents.length === 0) {
      // Show all data when no content is selected
      this.dataSource.data = this.allData;
    } else if (this.selectedContents.includes('selectAll')) {
      // Show all data when "Select All" is selected
      this.dataSource.data = this.allData;
      // Remove "selectAll" from the array and keep only actual content
      this.selectedContents = this.uniqueContents.slice();
    } else {
      // Filter data by selected contents (multiple)
      this.dataSource.data = this.allData.filter((item: GroupSessionTopics) =>
        this.selectedContents.includes(item.content)
      );
    }
  }

  applyFilter(event: KeyboardEvent) {
    const input = event.target as HTMLInputElement;
    this.dataSource.filter = input.value.trim().toLowerCase();
  }

  toggleSelectAll(event: any): void {
    event.stopPropagation();

    if (this.selectedContents.length === this.uniqueContents.length) {
      // Deselect all
      this.selectedContents = [];
    } else {
      // Select all
      this.selectedContents = this.uniqueContents.slice();
    }

    this.filterByContent();
  }

  clearFilters(): void {
    this.selectedContents = [];
    this.dataSource.data = this.allData;
  }

  addInduction() {
    this.dialog.open(AddEditGroupSessionTopicsComponent, {
      width: '900px',
      height: '95vh',
      disableClose: true,
      data: null
    }).afterClosed().subscribe((res:any) => {
      if (res === 'saved') {
        this.loadList();
      }
    });
  }

  edit(row: GroupSessionTopics) {
    this.dialog.open(AddEditGroupSessionTopicsComponent, {
      width: '900px',
      height: '95vh',
      disableClose: true,
      data: row
    }).afterClosed().subscribe((res:any) => {
      if (res === 'updated') {
        this.loadList();
      }
    });
  }



  view(row: any) {
    // ✅ Add activity log before navigation
    const activity = {
      date: new Date().getTime(),
      section: 'Group Session Topics',
      action: 'Viewed',
      description: `Group Session Topics viewed by user`,
      currentIp: localStorage.getItem('currentip')!
    };
    this.mLogService.addLog(activity);

    // ✅ Navigate to view page
    this.router.navigate(['/home/counseling/group-session-topics-view', row.id]);
  }



  delete(id: string | undefined) {
    if (!id) return;

    if (confirm('Are you sure to delete this Group Session Topic?')) {
      this.groupSessionService.deleteRow(id)
        .then(() => {
          alert('Group Session Topic deleted successfully');

          // ✅ Add activity log
          const activity = {
            date: new Date().getTime(),
            section: 'Group Session Topics',
            action: 'Deleted',
            description: `Group Session Topics deleted by user`,
            currentIp: localStorage.getItem('currentip')!
          };
          this.mLogService.addLog(activity);
        })
        .catch((error) => {
          console.error('Error deleting Group Session Topic:', error);
          alert('Error deleting Group Session Topic!');
        });
    }
  }

  extractFileName(url: string): string {
    const decoded = decodeURIComponent(url); // remove %2F etc.
    const fileWithQuery = decoded.split('/').pop() || "";
    return fileWithQuery.split('?')[0] ; // remove ?token
  }

  isArray(value: any): boolean {
    return Array.isArray(value);
  }

  protected readonly PermissionType = PermissionType;

  protected readonly Array = Array;

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }
}
